import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Alert from '@mui/material/Alert';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DataSourcePreviewComponent } from '../DataSourcePreviewComponent';
import { EventDataChannel } from 'types/api/Tenant/EventDataSourceTypes';
import { Avatar, Box } from '@mui/material';

const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 1100,
  height: '75vh',
  maxHeight: 700,
  bgcolor: 'background.paper',
  borderRadius: 2.5,
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer', fontSize: 26 };

export type ModalChannelPreviewDataState = {
  open: boolean;
  key?: string | number;
  selectedChannel?: EventDataChannel;
};

export interface ModalChannelPreviewDataProps {
  visible?: boolean;
  onClose?: () => void;
  selectedChannel?: EventDataChannel;
  onConnectChannel?: (selectedChannel: EventDataChannel) => void;
}

export default function ModalChannelPreviewData(props: ModalChannelPreviewDataProps) {
  const { onClose, visible = false, selectedChannel, onConnectChannel } = props;

  const handleOnClickConnect = () => {
    if (!selectedChannel) return;
    onConnectChannel?.(selectedChannel);
  };
  if (!selectedChannel) return null;

  return (
    <Modal open={visible} onClose={onClose}>
      <Stack direction='column' sx={ModalContentStyle}>
        {/* Header */}
        <Stack
          justifyContent='space-between'
          direction='row'
          alignItems='center'
          px={3}
          py={1.5}
          sx={{ bgcolor: '#FAFBFD', borderBottom: '1px solid #E3EBF6' }}
        >
          <Stack direction='row' alignItems='center' gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 34,
                height: 34,
                borderRadius: '8px',
                bgcolor: '#EDF0FF',
              }}
            >
              <VisibilityOutlinedIcon sx={{ color: '#3B4797', fontSize: 18 }} />
            </Box>
            <Stack>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797', fontSize: 15 }}>
                Preview Data
              </Typography>
              <Typography variant='body2' sx={{ color: '#828C99', fontSize: 12 }}>
                {selectedChannel.name}
              </Typography>
            </Stack>
          </Stack>
          <IconButton onClick={onClose} size='small' sx={{ '&:hover': { bgcolor: '#FEE2E2' } }}>
            <HighlightOffRoundedIcon sx={closeIconStyle} />
          </IconButton>
        </Stack>

        {/* Content */}
        <Stack flex={1} justifyContent='space-between' px={3} py={2} overflow='auto'>
          <Stack gap={2}>
            <Alert severity='info' variant='outlined' sx={{ borderRadius: 1.5 }}>
              <Stack direction='row' alignItems='center' gap={2}>
                {selectedChannel.logoUrl && (
                  <Avatar
                    variant='square'
                    alt={selectedChannel.name}
                    src={selectedChannel.logoUrl}
                    imgProps={{ style: { objectFit: 'inherit' } }}
                    sx={{ width: 140, height: 28, mr: 1, objectFit: 'contain' }}
                  />
                )}
                <Typography variant='body2' sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  Below is a sample of the data extracted from <b>{selectedChannel.name}</b>. Review the data to ensure
                  accuracy and relevance, then click "Connect Channel" to proceed with mapping.
                </Typography>
              </Stack>
            </Alert>
            <DataSourcePreviewComponent dataChannelId={selectedChannel.id} />
          </Stack>
        </Stack>

        {/* Footer */}
        <Stack
          direction='row'
          gap={1.5}
          justifyContent='flex-end'
          px={3}
          py={1.5}
          sx={{ borderTop: '1px solid #E3EBF6', bgcolor: '#FAFBFD' }}
        >
          <Button variant='main-table-panel-border' onClick={onClose}>
            Close
          </Button>
          <Button variant='main-table-panel' onClick={handleOnClickConnect} endIcon={<EastOutlinedIcon />}>
            Connect Channel
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
